import { AxiosRequestConfig, AxiosResponse } from 'axios'

import { Api } from 'common/api/api'
import { apiConfig } from 'common/api/api.config'
import { PaginatedResponse } from 'common/types/managers'
import GenericPaginationParams from 'common/types/genericPaginationParams'
import {
  Process,
  ProcessResponse,
  CreateProcessParams,
  DuplicateProcessParams,
  ChangeStatusData,
  Status,
  StatusOption,
  TaskStatusType,
  RestartProcessParams,
  ExportProcessesResponse,
  ImportProcessesResponse,
  TaskAgentRunState
} from 'pages/processManager/types'

const ENDPOINT = '/process'

export class ProcessApi extends Api {
  public constructor(config: AxiosRequestConfig) {
    super(config)
    // this middleware is been called right before the http request is made.
    this.interceptors.request.use((param: AxiosRequestConfig) => ({
      ...param
    }))
    // this middleware is been called right before the response is get it by the method that triggers the request
    this.interceptors.response.use((param: AxiosResponse) => ({
      ...param
    }))
    Object.setPrototypeOf(this, ProcessApi.prototype)
  }

  public async getAllProcess(
    params?: GenericPaginationParams
  ): Promise<PaginatedResponse<Process>> {
    try {
      const res: AxiosResponse<PaginatedResponse<Process>> = await this.get<
        Process,
        AxiosResponse<PaginatedResponse<Process>>
      >(`${ENDPOINT}/`, { params: { ...params } })

      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async createProcess(data: CreateProcessParams): Promise<boolean> {
    try {
      const res: AxiosResponse<boolean> = await this.post(`${ENDPOINT}/`, data, {
        headers: { 'Content-Type': 'application/json' }
      })
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async updateProcess(data: CreateProcessParams, id?: string): Promise<boolean> {
    try {
      const res: AxiosResponse<boolean> = await this.put(`${ENDPOINT}/${id}/`, data, {
        headers: { 'Content-Type': 'application/json' }
      })
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async getProcessById(id: string): Promise<ProcessResponse> {
    try {
      const res: AxiosResponse<ProcessResponse> = await this.get(`${ENDPOINT}/${id}/`)
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async deleteProcess(id: string): Promise<void> {
    try {
      const res: AxiosResponse<void> = await this.delete(`${ENDPOINT}/${id}/`)
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async duplicateProcess(
    id: string,
    data: DuplicateProcessParams
  ): Promise<ProcessResponse> {
    try {
      const res: AxiosResponse<ProcessResponse> = await this.post(
        `${ENDPOINT}/${id}/duplicateProcess/`,
        data
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async restartProcess(id: string, data: RestartProcessParams): Promise<ProcessResponse> {
    try {
      const res: AxiosResponse<ProcessResponse> = await this.post(
        `${ENDPOINT}/${id}/restartProcess/`,
        data
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async getProcessByUser(id: string): Promise<ProcessResponse[]> {
    try {
      const res: AxiosResponse<ProcessResponse[]> = await this.get(
        `${ENDPOINT}/${id}/getProcessByUser/`
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async getProcessStatus(): Promise<Status[]> {
    try {
      const res: AxiosResponse<Status[]> = await this.get(`${ENDPOINT}/getProcessStatus/`)
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  public async getTaskStatus(): Promise<StatusOption<TaskStatusType>[]> {
    try {
      const res: AxiosResponse<StatusOption<TaskStatusType>[]> = await this.get(
        `${ENDPOINT}/getTaskStatus/`
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async changeTaskStatus(id: string, data: ChangeStatusData): Promise<ProcessResponse[]> {
    try {
      const res: AxiosResponse<ProcessResponse[]> = await this.put(
        `${ENDPOINT}/${id}/changeTaskStatus/`,
        data
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async exportProcesses(processIds: string[]): Promise<ExportProcessesResponse<string>> {
    try {
      const data = { process_ids: processIds }
      const res: AxiosResponse<ExportProcessesResponse<string>> = await this.post(
        `${ENDPOINT}/exportProcesses/`,
        data,
        {
          responseType: 'blob'
        }
      )
      return { data: this.success<any>(res), headers: res.headers }
    } catch (error) {
      throw error
    }
  }
  public async importProcesses(payload: any): Promise<ImportProcessesResponse> {
    try {
      const res: AxiosResponse<ImportProcessesResponse> = await this.post(
        `${ENDPOINT}/importProcesses/`,
        payload
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  /**
   * Queue an agent run for a task. The id is the TASK id: this endpoint follows
   * `changeTaskStatus` above, which addresses tasks the same way.
   */
  public async runTaskAgent(taskId: string | number): Promise<{ queued: string[] }> {
    try {
      const res: AxiosResponse<{ queued: string[] }> = await this.post(
        `${ENDPOINT}/${taskId}/runTaskAgent/`,
        {}
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  /**
   * Whether the user is in an active process of the open application.
   *
   * A boolean and not the process list: this only decides whether the task
   * board is offered, and it is asked again on every process event — the list
   * serialises every task of every process, which is far too much for that.
   */
  public async hasActiveProcess(): Promise<boolean> {
    try {
      const res: AxiosResponse<{ has_active_process: boolean }> = await this.get(
        `${ENDPOINT}/hasActiveProcess/`
      )
      return this.success(res).has_active_process
    } catch (error) {
      throw error
    }
  }
  /** Which agents are working on a task right now, and what they are doing. */
  public async getTaskAgentRunState(taskId: string | number): Promise<TaskAgentRunState> {
    try {
      const res: AxiosResponse<TaskAgentRunState> = await this.get(
        `${ENDPOINT}/${taskId}/getTaskAgentRunState/`
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
}

export const processApi = new ProcessApi(apiConfig)
